import { useCallback, useEffect, useState } from "react";
import { api } from "services/api.service";

export type LoginEvent = {
  id: number;
  userName?: string;
  userEmail?: string;
  ipAddress?: string | null;
  deviceSummary?: string | null;
  loginMethod: string;
  createdAt: string;
};

type SecurityResponse = {
  emailMfaRequired: boolean;
  loginEvents: LoginEvent[];
  meta: { totalPages: number; count: number };
};

export function loginMethodLabel(method: string): string {
  switch (method) {
    case "mfa":
      return "Email code";
    case "magic_link":
      return "Magic link";
    case "password_reset":
      return "Password reset";
    case "password":
      return "Password";
    default:
      return method;
  }
}

export default function useSecurity() {
  const [emailMfaRequired, setEmailMfaRequired] = useState(false);
  const [loginEvents, setLoginEvents] = useState<LoginEvent[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const pageSize = 25;

  const applyResponse = (data: SecurityResponse) => {
    setEmailMfaRequired(Boolean(data.emailMfaRequired));
    setLoginEvents(data.loginEvents || []);
    setCount(data.meta?.count || 0);
  };

  const load = useCallback((nextPage = 0) => {
    setIsLoading(true);
    api
      .get<SecurityResponse>("/security", {
        params: { page: { number: nextPage + 1, size: pageSize } },
      })
      .then((res) => applyResponse(res.data))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    load(0);
  }, [load]);

  const updateEmailMfaRequired = useCallback(async (enabled: boolean) => {
    setIsSaving(true);
    try {
      const { data } = await api.patch<SecurityResponse>("/security", {
        emailMfaRequired: enabled,
      });
      applyResponse(data);
    } finally {
      setIsSaving(false);
    }
  }, []);

  return {
    emailMfaRequired,
    loginEvents,
    count,
    page,
    setPage,
    pageSize,
    isLoading,
    isSaving,
    load,
    updateEmailMfaRequired,
  };
}
